<ng-template let-extraClass="extraClass" #topNav>
  @if (!windowService.isDesktopRes) {
    <tidy-top-nav [class]="extraClass" [title]="'Optimizer'" [canGoBack]="true" [customBack]="'more'" [showLoadingSpinner]="isSaving()" />
  }
</ng-template>
@if (!embedded()) {
  <ng-container *ngTemplateOutlet="topNav;context:{ extraClass: 'mobile' }" />
}

<ng-template #optimizerActiveAlert>
  <div class="opt-hero">
    <span class="opt-hero__pulse" aria-hidden="true"></span>
    <span class="opt-hero__phrase">
      <tidy-text class="opt-hero__verbose" [body]="'Optimizer'" />
      <tidy-text class="opt-hero__cap-on-narrow" [body]="'active'" />
    </span>
    <span class="opt-hero__sep">·</span>
    <span class="opt-hero__phrase">
      <tidy-text [body]="daysOptimized + ' ' + ('days' | translate)" />
      <tidy-text class="opt-hero__verbose" [body]="'being optimized'" />
    </span>
    <span class="opt-hero__sep">·</span>
    <span class="opt-hero__phrase">
      <tidy-text class="opt-hero__verbose" [body]="'Last optimized'" />
      <tidy-text [body]="lastOptimizedLabel()" />
    </span>
  </div>
</ng-template>

<!--
  Subsection registry — every searchable unit on the page is defined here once
  and rendered by id. In the normal layout (`@if (!isSearching)`) the cards
  embed their subsections via `*ngTemplateOutlet`; while searching, only the
  matched subsections render (see `@if (isSearching)` block below).

  The <ng-container [formGroup]> wrapper gives every embedded view its own
  FormGroupDirective: embedded views resolve DI from their template scope, so
  without it every `formControlName` inside logs "addControl of null" at
  mount (the inputs only worked via their explicit `[form]` bindings).
-->
<ng-template #sub let-id="id">
  <ng-container [formGroup]="bookingForm">
    @switch (id) {
    @case ('calendar') {
      <div class="bs-calendar-block">
        <optimizer-section [address]="selectedAddress" [compact]="true" [mock]="isDemo">
          <div above-calendar class="bs-price-toolbar">
            <tidy-image
              (click)="prevPriceMonth()"
              [src]="'assets/icon/arrowhead.png'"
              class="body-size arrow-icon reverse-arrow" />
            <tidy-text [header]="priceMonthRangeLabel" />
            <tidy-image
              (click)="nextPriceMonth()"
              [src]="'assets/icon/arrowhead.png'"
              class="body-size arrow-icon last-arrow" />
          </div>
        </optimizer-section>
      </div>
    }

    @case ('strategy') {
      <rental-types-settings
        [value]="rentalTypesSeed"
        [staySync]="syncDot('min_stay', 'max_stay')"
        [bookingAdvanceSync]="syncDot('max_booking_advance_months')"
        [dateOverridesSync]="dateOverridesSync"
        (valueChange)="onRentalTypesChange($event)" />
    }

    @case ('price-range') {
      <div class="bs-card">
        <div class="bs-price-limits__header">
          <span class="bs-label-dot">
            <tidy-text [body]="'Price range'" class="bs-row__label bold" />
            <sync-status-dot [sync]="priceSyncDot()" />
          </span>
          <tidy-text [helper]="'Base is the starting nightly price; min and max are the floor and ceiling we’ll never price outside of, regardless of demand.'" class="bs-row__sub" />
          @if (missingBasePrice()) {
            <tidy-text [body]="'Add a base price. We use it for any night without a suggested price.'" class="red" data-testid="missing-base-price" />
          }
        </div>
        <div class="bs-pair">
          <tidy-input
            [label]="'Base'"
            [type]="'number'"
            [form]="bookingForm.controls.basePrice"
            [formControl]="bookingForm.controls.basePrice"
            [submitted]="submitted"
            [errorMessage]="fieldErrors.basePrice"
            [icon]="'assets/svg/cash-outline.svg'" />
          <tidy-input
            [label]="'Min'"
            [type]="'number'"
            [form]="bookingForm.controls.minPrice"
            [formControl]="bookingForm.controls.minPrice"
            [submitted]="submitted"
            [errorMessage]="fieldErrors.minPrice"
            [icon]="'assets/svg/cash-outline.svg'" />
          <tidy-input
            [label]="'Max'"
            [type]="'number'"
            [form]="bookingForm.controls.maxPrice"
            [formControl]="bookingForm.controls.maxPrice"
            [submitted]="submitted"
            [errorMessage]="fieldErrors.maxPrice"
            [icon]="'assets/svg/cash-outline.svg'" />
        </div>
        <!-- The listing builder shows its own security deposit, so the embedded copy leaves this one out. -->
        @if (!embedded()) {
          <div class="bs-price-limits__header" style="margin-top: 16px;">
            <tidy-text [body]="'Security deposit'" class="bs-row__label bold" />
            <tidy-text [helper]="'Held from direct-booking guests and returned after checkout unless damages are recorded. Direct booking only. Whole dollars; clear the field to remove it.'" class="bs-row__sub" />
          </div>
          <div class="bs-pair">
            <tidy-input
              [label]="'Deposit'"
              [type]="'number'"
              [form]="bookingForm.controls.securityDeposit"
              [formControl]="bookingForm.controls.securityDeposit"
              [icon]="'assets/svg/cash-outline.svg'" />
          </div>
        }
      </div>
    }

    @case ('channel-instant-book') {
      <div class="bs-card">
        <div class="bs-row">
          <div class="bs-row__main">
            <span class="bs-label-dot">
              <tidy-text [body]="'Instant Book'" class="bs-row__label bold" />
              <sync-status-dot [sync]="syncDot('instant_book_mode')" />
              <platform-pills [key]="'instant_book_mode'" />
            </span>
            <tidy-text [helper]="'Lets guests book without pre-approval.'" class="bs-row__sub" />
          </div>
          <tidy-toggle [toggled]="instantBook" (toggleChanged)="instantBook = !instantBook; markDirty()" />
        </div>
        @if (instantBook) {
          <div class="bs-sub">
            <div class="bs-sub__row">
              <tidy-text [body]="'Require good track record'" />
              <tidy-toggle [toggled]="instantTrackRecord" (toggleChanged)="instantTrackRecord = !instantTrackRecord; markDirty()" />
            </div>
          </div>
        }
      </div>
    }

    @case ('channel-non-refundable') {
      <div class="bs-card">
        <div class="bs-row">
          <div class="bs-row__main">
            <span class="bs-label-dot">
              <tidy-text [body]="'Non-Refundable Rate'" class="bs-row__label bold" />
              <sync-status-dot [sync]="syncDot('non_refundable_rate')" />
              <platform-pills [key]="'non_refundable_rate'" />
            </span>
            <tidy-text [helper]="'Offer a discounted, non-refundable option on Airbnb and Booking.com.'" class="bs-row__sub" />
          </div>
          <tidy-toggle [toggled]="nonRefundableRate" (toggleChanged)="nonRefundableRate = !nonRefundableRate; markDirty()" />
        </div>
      </div>
    }

    @case ('channel-seasonal-cancellation') {
      <seasonal-cancellation-settings
        [value]="seasonalCancellationSeed"
        [sync]="syncDot('seasonal_cancellation')"
        (valueChange)="onSeasonalCancellationChange($event)" />
    }

    @case ('rank-tracker') {
      <rank-tracker-settings />
    }

    @case ('discount-weekly') {
      <div class="bs-discount">
        <div class="bs-discount__head">
          <span class="bs-label-dot">
            <tidy-text [body]="'Weekly stays'" class="bs-discount__label bold" />
            <sync-status-dot [sync]="syncDot('discount_weekly')" />
            <platform-pills [key]="'discount_weekly'" />
          </span>
          <tidy-toggle [toggled]="discountWeeklyOn" (toggleChanged)="discountWeeklyOn = !discountWeeklyOn; markDirty()" />
        </div>
        @if (discountWeeklyOn) {
          <div class="bs-discount__body">
            <tidy-input
              [label]="'Discount %'"
              [type]="'number'"
              [form]="bookingForm.controls.discountWeekly"
              [formControl]="bookingForm.controls.discountWeekly"
              [submitted]="submitted"
              [errorMessage]="fieldErrors.discountWeekly" />
          </div>
        }
      </div>
    }

    @case ('discount-monthly') {
      <div class="bs-discount">
        <div class="bs-discount__head">
          <span class="bs-label-dot">
            <tidy-text [body]="'Monthly stays'" class="bs-discount__label bold" />
            <sync-status-dot [sync]="syncDot('discount_monthly')" />
            <platform-pills [key]="'discount_monthly'" />
          </span>
          <tidy-toggle [toggled]="discountMonthlyOn" (toggleChanged)="discountMonthlyOn = !discountMonthlyOn; markDirty()" />
        </div>
        @if (discountMonthlyOn) {
          <div class="bs-discount__body">
            <tidy-input
              [label]="'Discount %'"
              [type]="'number'"
              [form]="bookingForm.controls.discountMonthly"
              [formControl]="bookingForm.controls.discountMonthly"
              [submitted]="submitted"
              [errorMessage]="fieldErrors.discountMonthly" />
          </div>
        }
      </div>
    }

    @case ('discount-last-minute') {
      <div class="bs-discount">
        <div class="bs-discount__head">
          <span class="bs-label-dot">
            <tidy-text [body]="'Last-minute booking'" class="bs-discount__label bold" />
            <sync-status-dot [sync]="syncDot('discount_last_minute')" />
            <platform-pills [key]="'discount_last_minute'" />
          </span>
          <tidy-toggle [toggled]="discountLastMinuteOn" (toggleChanged)="discountLastMinuteOn = !discountLastMinuteOn; markDirty()" />
        </div>
        @if (discountLastMinuteOn) {
          <div class="bs-discount__body bs-discount__body--pair">
            <tidy-input
              [label]="'Discount %'"
              [type]="'number'"
              [form]="bookingForm.controls.discountLastMinute"
              [formControl]="bookingForm.controls.discountLastMinute"
              [submitted]="submitted"
              [errorMessage]="fieldErrors.discountLastMinute" />
            <tidy-input
              [label]="'Days before check-in'"
              [type]="'number'"
              [form]="bookingForm.controls.discountLastMinuteDays"
              [formControl]="bookingForm.controls.discountLastMinuteDays"
              [submitted]="submitted"
              [errorMessage]="fieldErrors.discountLastMinuteDays" />
          </div>
        }
      </div>
    }

    @case ('discount-far-out') {
      <div class="bs-discount">
        <div class="bs-discount__head">
          <span class="bs-label-dot">
            <tidy-text [body]="'Far-out booking'" class="bs-discount__label bold" />
            <sync-status-dot [sync]="syncDot('discount_far_out')" />
            <platform-pills [key]="'discount_far_out'" />
          </span>
          <tidy-toggle [toggled]="discountFarOutOn" (toggleChanged)="discountFarOutOn = !discountFarOutOn; markDirty()" />
        </div>
        @if (discountFarOutOn) {
          <div class="bs-discount__body bs-discount__body--pair">
            <tidy-input
              [label]="'Discount %'"
              [type]="'number'"
              [form]="bookingForm.controls.discountFarOut"
              [formControl]="bookingForm.controls.discountFarOut"
              [submitted]="submitted"
              [errorMessage]="fieldErrors.discountFarOut" />
            <tidy-input
              [label]="'Days in advance'"
              [type]="'number'"
              [form]="bookingForm.controls.discountFarOutDays"
              [formControl]="bookingForm.controls.discountFarOutDays"
              [submitted]="submitted"
              [errorMessage]="fieldErrors.discountFarOutDays" />
          </div>
        }
      </div>
    }

    @case ('discount-member-only') {
      <div class="bs-discount">
        <div class="bs-discount__head">
          <span class="bs-label-dot">
            <tidy-text [body]="'Member-only exclusive'" class="bs-discount__label bold" />
            <sync-status-dot [sync]="syncDot('discount_member_only')" />
            <platform-pills [key]="'discount_member_only'" />
          </span>
          <tidy-toggle [toggled]="discountMemberOnlyOn" (toggleChanged)="discountMemberOnlyOn = !discountMemberOnlyOn; markDirty()" />
        </div>
        @if (discountMemberOnlyOn) {
          <div class="bs-discount__body">
            <tidy-input
              [label]="'Discount %'"
              [type]="'number'"
              [form]="bookingForm.controls.discountMemberOnly"
              [formControl]="bookingForm.controls.discountMemberOnly"
              [submitted]="submitted"
              [errorMessage]="fieldErrors.discountMemberOnly" />
          </div>
        }
      </div>
    }

    @case ('discount-mobile') {
      <div class="bs-discount">
        <div class="bs-discount__head">
          <span class="bs-label-dot">
            <tidy-text [body]="'Mobile-only rate'" class="bs-discount__label bold" />
            <sync-status-dot [sync]="syncDot('discount_mobile')" />
            <platform-pills [key]="'discount_mobile'" />
          </span>
          <tidy-toggle [toggled]="discountMobileOn" (toggleChanged)="discountMobileOn = !discountMobileOn; markDirty()" />
        </div>
        @if (discountMobileOn) {
          <div class="bs-discount__body">
            <tidy-input
              [label]="'Discount %'"
              [type]="'number'"
              [form]="bookingForm.controls.discountMobile"
              [formControl]="bookingForm.controls.discountMobile"
              [submitted]="submitted"
              [errorMessage]="fieldErrors.discountMobile" />
          </div>
        }
      </div>
    }

    @case ('discount-flash') {
      <div class="bs-discount">
        <div class="bs-discount__head">
          <span class="bs-label-dot">
            <tidy-text [body]="'Flash / campaign deal'" class="bs-discount__label bold" />
            <sync-status-dot [sync]="syncDot('discount_flash')" />
            <platform-pills [key]="'discount_flash'" />
          </span>
          <tidy-toggle [toggled]="discountFlashOn" (toggleChanged)="discountFlashOn = !discountFlashOn; markDirty()" />
        </div>
        @if (discountFlashOn) {
          <div class="bs-discount__body">
            <tidy-input
              [label]="'Discount %'"
              [type]="'number'"
              [form]="bookingForm.controls.discountFlash"
              [formControl]="bookingForm.controls.discountFlash"
              [submitted]="submitted"
              [errorMessage]="fieldErrors.discountFlash" />
          </div>
        }
      </div>
    }

    @case ('minstay-checkin-checkout') {
      <div class="bs-card">
        <div class="bs-row">
          <div class="bs-row__main">
            <span class="bs-label-dot">
              <tidy-text [body]="'Check-in / Check-out times'" class="bs-row__label bold" />
              <sync-status-dot [sync]="syncDot('check_in_after', 'check_out_by')" />
              <platform-pills [key]="'check_in_after'" />
            </span>
            <tidy-text [helper]="'When guests arrive and depart.'" class="bs-row__sub" />
          </div>
        </div>
        <div class="bs-pair">
          <tidy-select
            [label]="'Check-in after'"
            [items]="timeSelectItems"
            [form]="bookingForm.controls.checkInAfter"
            [formControl]="bookingForm.controls.checkInAfter" />
          <tidy-select
            [label]="'Check-out by'"
            [items]="timeSelectItems"
            [form]="bookingForm.controls.checkOutBy"
            [formControl]="bookingForm.controls.checkOutBy" />
        </div>
      </div>
    }

    @case ('minstay-scheduling') {
      <div class="bs-card">
        <div class="bs-row">
          <div class="bs-row__main">
            <span class="bs-label-dot">
              <tidy-text [body]="'Restricted check-in / check-out days'" class="bs-row__label bold" />
              <sync-status-dot [sync]="syncDot('closed_to_arrival_weekdays', 'closed_to_departure_weekdays', 'advance_notice_days', 'prep_time')" />
              <platform-pills [key]="'closed_to_arrival_weekdays'" />
            </span>
            <tidy-text [helper]="'Block specific weekdays from being arrival or departure days.'" class="bs-row__sub" />
          </div>
        </div>
        <div class="bs-weekdays">
          @for (d of weekdays; track d) {
            <button type="button" class="bs-weekday"
              [class.bs-weekday--off]="restrictedDays[d]"
              (click)="toggleDay(d)">
              <tidy-text [body]="d" />
            </button>
          }
        </div>

        <div class="bs-pair">
          <div class="bs-field">
            <tidy-input
              [label]="'Advance notice (days)'"
              [type]="'number'"
              [form]="bookingForm.controls.advanceNotice"
              [formControl]="bookingForm.controls.advanceNotice" />
            <platform-pills [key]="'advance_notice_days'" />
          </div>
          <div class="bs-field">
            <tidy-input
              [label]="'Preparation time (nights)'"
              [type]="'number'"
              [form]="bookingForm.controls.prepTime"
              [formControl]="bookingForm.controls.prepTime" />
            <platform-pills [key]="'prep_time'" />
          </div>
        </div>
      </div>
    }

    <!-- The listing builder's basic fields, read only here: the builder edits them. -->
    @case ('listing-content') {
      <div class="bs-card">
        @for (field of listingContentRows(); track field.key) {
          <div class="bs-row">
            <div class="bs-row__main">
              <span class="bs-label-dot">
                <tidy-text [body]="field.label" class="bs-row__label bold" />
                <sync-status-dot [sync]="listingSyncDot(field.key)" />
              </span>
              <tidy-text [helper]="field.value" class="bs-row__sub bs-listing-content__value" />
            </div>
          </div>
        }
        <tidy-text [actionBody]="'Edit in listing builder'" (action)="openListingBuilder()" class="link" />
      </div>
    }

    @case ('listing-pet-friendly') {
      <div class="bs-card">
        <div class="bs-row">
          <div class="bs-row__main">
            <span class="bs-label-dot">
              <tidy-text [body]="'Pet-friendly'" class="bs-row__label bold" />
              <sync-status-dot [sync]="syncDot('pets_allowed')" />
            </span>
            <tidy-text [helper]="'Allow pets across all channels and surface the appropriate listing badges.'" class="bs-row__sub" />
          </div>
          <tidy-toggle [toggled]="petFriendly" (toggleChanged)="petFriendly = !petFriendly; markDirty()" />
        </div>
        @if (petFriendly) {
          <div class="bs-pair">
            <div class="bs-field">
              <tidy-input
                [label]="'Pet fee'"
                [type]="'number'"
                [form]="bookingForm.controls.petFee"
                [formControl]="bookingForm.controls.petFee"
                [icon]="'assets/svg/cash-outline.svg'" />
              <platform-pills [key]="'pet_fee'" />
            </div>
          </div>
        }
      </div>
    }

    <!-- The listing builder's own Pets Allowed amenity carries the toggle, so it embeds only the fee. -->
    @case ('listing-pet-fee') {
      <div class="bs-card">
        <div class="bs-row">
          <div class="bs-row__main">
            <span class="bs-label-dot">
              <tidy-text [body]="'Pet fee'" class="bs-row__label bold" />
              <sync-status-dot [sync]="syncDot('pet_fee')" />
              <platform-pills [key]="'pet_fee'" />
            </span>
            <tidy-text [helper]="'Charged when Pets Allowed is on in Amenities.'" class="bs-row__sub" />
          </div>
        </div>
        <div class="bs-pair">
          <tidy-input
            [label]="'Pet fee'"
            [type]="'number'"
            [form]="bookingForm.controls.petFee"
            [formControl]="bookingForm.controls.petFee"
            [icon]="'assets/svg/cash-outline.svg'" />
        </div>
      </div>
    }

    @case ('listing-cancellation-policy') {
      <div class="bs-card">
        <div class="bs-row bs-row--policy">
          <div class="bs-row__main">
            <span class="bs-label-dot">
              <tidy-text [body]="'Cancellation policy'" class="bs-row__label bold" />
              <sync-status-dot [sync]="syncDot('cancellation_policy')" />
            </span>
            <tidy-text [helper]="'Synced across all channels — stricter policies usually lift target price.'" class="bs-row__sub" />
          </div>
          <div class="bs-policy">
          @for (p of cancellationPolicies; track p.value) {
            <button type="button" class="bs-policy__chip"
              [class.bs-policy__chip--on]="cancellationPolicy === p.value"
              (click)="cancellationPolicy = p.value; markDirty()">
              <tidy-text [body]="p.label" />
            </button>
          }
          </div>
        </div>
      </div>
    }

    <!-- ============== Longer-term optimizer group ============== -->

    @case ('ml-comps') {
      <div class="bs-card">
        <div class="bs-row">
          <div class="bs-row__main">
            <tidy-text [body]="'Comps'" class="bs-row__label bold" />
            <tidy-text [helper]="'Nearby comparable listings pulled from the mid-term and long-term channels.'" class="bs-row__sub" />
          </div>
          <div class="ml-segmented">
            <button type="button" class="ml-segmented__opt"
              [class.ml-segmented__opt--on]="compsTerm === 'mid'"
              (click)="setCompsTerm('mid')">
              <tidy-text [body]="'Mid-term (furnished)'" />
            </button>
            <button type="button" class="ml-segmented__opt"
              [class.ml-segmented__opt--on]="compsTerm === 'long'"
              (click)="setCompsTerm('long')">
              <tidy-text [body]="'Long-term'" />
            </button>
          </div>
        </div>

        <div class="ml-comps-list">
          @if (comps.length === 0) {
            <tidy-text [helper]="'No comparable listings pulled for this property yet.'" />
          }
          @for (c of comps; track c.id) {
            <div class="ml-comp">
              <div class="ml-comp__main">
                <tidy-text [body]="c.name" class="ml-comp__name bold" />
                <div class="ml-comp__meta">
                  <span class="ml-source-pill"><tidy-text [body]="c.source" /></span>
                  <tidy-text [helper]="c.beds + (' bd · ' | translate) + c.baths + (' ba · ' | translate) + c.sqft + (' sqft' | translate)" />
                  <tidy-text [helper]="c.distanceMi + (' mi away' | translate)" />
                  <tidy-text [helper]="c.daysOnMarket + (' days on market' | translate)" />
                </div>
              </div>
              <tidy-text [body]="(c.monthlyCents | formatCentsToDollars) + ('/mo' | translate)" class="ml-comp__price bold" />
            </div>
          }
        </div>

        @if (comps.length > 0) {
          <div class="ml-summary-row">
            <tidy-text [body]="('Median comp: ' | translate) + (compsSummary.medianCents | formatCentsToDollars) + ('/mo' | translate)" class="bold" />
            <tidy-text [helper]="('Suggested range: ' | translate) + (compsSummary.lowCents | formatCentsToDollars) + ' – ' + (compsSummary.highCents | formatCentsToDollars) + ('/mo' | translate)" />
          </div>
        }
      </div>
    }

    @case ('ml-target-rent') {
      <div class="bs-card">
        <div class="bs-row__main">
          <tidy-text [body]="'Target rent'" class="bs-row__label bold" />
          <tidy-text [helper]="'Editable monthly targets and floors for each term, with a comp-derived recommendation.'" class="bs-row__sub" />
        </div>

        <form [formGroup]="targetForm" class="ml-target-grid">
          <div class="ml-target-col">
            <tidy-text [body]="'Mid-term'" class="ml-target-col__title bold" />
            <tidy-input
              [label]="'Target monthly ($)'"
              [type]="'number'"
              [form]="targetForm.controls.midTarget"
              formControlName="midTarget"
              [icon]="'assets/svg/cash-outline.svg'" />
            <tidy-input
              [label]="'Floor monthly ($)'"
              [type]="'number'"
              [form]="targetForm.controls.midFloor"
              formControlName="midFloor"
              [icon]="'assets/svg/cash-outline.svg'" />
            @if (midRecommendedLabel) {
              <tidy-text [helper]="('Recommended from comps: ' | translate) + midRecommendedLabel" class="ml-recommended" />
            }
          </div>
          <div class="ml-target-col">
            <tidy-text [body]="'Long-term'" class="ml-target-col__title bold" />
            <tidy-input
              [label]="'Target monthly ($)'"
              [type]="'number'"
              [form]="targetForm.controls.longTarget"
              formControlName="longTarget"
              [icon]="'assets/svg/cash-outline.svg'" />
            <tidy-input
              [label]="'Floor monthly ($)'"
              [type]="'number'"
              [form]="targetForm.controls.longFloor"
              formControlName="longFloor"
              [icon]="'assets/svg/cash-outline.svg'" />
            @if (longRecommendedLabel) {
              <tidy-text [helper]="('Recommended from comps: ' | translate) + longRecommendedLabel" class="ml-recommended" />
            }
          </div>
        </form>
      </div>
    }

    @case ('ml-listing-optimization') {
      <div class="bs-card">
        <div class="bs-row__main">
          <tidy-text [body]="'Listing optimization'" class="bs-row__label bold" />
          <tidy-text [helper]="'Per-channel completeness and status across the mid/long-term listing sites.'" class="bs-row__sub" />
        </div>

        <div class="ml-channels">
          @for (row of channelRows; track row.channel) {
            <div class="ml-channel">
              <div class="ml-channel__head">
                <tidy-text [body]="row.channel" class="bold" />
                <span class="ml-status-pill"
                  [class.ml-status-pill--listed]="row.status === 'Listed'"
                  [class.ml-status-pill--paused]="row.status === 'Paused'"
                  [class.ml-status-pill--not-listed]="row.status === 'Not listed'">
                  <tidy-text [body]="row.status" />
                </span>
                <tidy-text [helper]="row.completenessPct + ('% complete' | translate)" class="ml-channel__pct" />
              </div>
              <div class="ml-channel__bar">
                <div class="ml-channel__bar-fill" [style.width.%]="row.completenessPct"></div>
              </div>
              @if (row.suggestions.length > 0) {
                <ul class="ml-channel__suggestions">
                  @for (s of row.suggestions; track s) {
                    <li><tidy-text [helper]="s" /></li>
                  }
                </ul>
              }
            </div>
          }
        </div>
      </div>
    }
    }
  </ng-container>
</ng-template>

<ng-template #content>
  <tidy-wrapper [isPrimaryLayer]="true">
    @if (windowService.isDesktopRes) {
      <tidy-desktop-header class="no-bottom-margin">
        <div class="bs-header">
          <tidy-text [title]="'Optimizer'" />
          @if (!noRealAddress()) {
            <form [formGroup]="addressForm" class="bs-header__select">
              <tidy-header-select
                [items]="addressSelectItems"
                [form]="addressForm.get('addressValue')"
                formControlName="addressValue"
                [icon]="'assets/svg/home.svg'"
                (optionChange)="onAddressChange($event)" />
            </form>
          }
          <div class="bs-header__alert">
            @if (isSaving()) {
              <ion-spinner name="lines" class="bs-header__saving" [title]="'Saving...' | translate" />
            }
            <ng-container *ngTemplateOutlet="optimizerActiveAlert" />
          </div>
        </div>
        <div class="non-wrappable-right-aligned-container">
          <automations-preview-button
            [tag]="'optimizer'" />
        </div>
      </tidy-desktop-header>
    } @else {
      <div class="bs-mobile-picker">
        @if (!noRealAddress()) {
          <form [formGroup]="addressForm">
            <tidy-header-select
              [items]="addressSelectItems"
              [form]="addressForm.get('addressValue')"
              formControlName="addressValue"
              [icon]="'assets/svg/home.svg'"
              (optionChange)="onAddressChange($event)" />
          </form>
        }
        <div class="bs-mobile-alert">
          <ng-container *ngTemplateOutlet="optimizerActiveAlert" />
        </div>
      </div>
    }

    <!-- First-load gate. The skeleton below the header floats in on landing;
         when `isLoaded` flips the real content replaces every skeleton in
         place, instantly and with no animation. The flag never flips back,
         so route-reuse can never replay the skeleton. -->
    @if (isLoaded()) {
    <form [formGroup]="bookingForm" class="bs-stack" [class.bs-scroll]="windowService.isDesktopRes">

      @if (noRealAddress()) {
      <!-- Real account with no property yet: prompt to add one instead of settings. -->
      <div class="bs-optimizations-head">
        <tidy-text [header]="'Optimization Settings'" class="bs-optimizations-head__title bold" />
      </div>
      <tidy-alert class="black-background">
        <tidy-row>
          <tidy-text [body]="'Please add a property to view settings.'" />
        </tidy-row>
        <tidy-row>
          <tidy-text [actionBody]="'Add a property'" (action)="goToProperties()" class="link" />
        </tidy-row>
      </tidy-alert>
      } @else {

      <!-- Top-of-page calendar — always visible. While searching the matched
           subsections render below the search input, but the calendar stays
           anchored at the top so the page layout doesn't shift. -->
      <ng-container *ngTemplateOutlet="sub; context: { id: 'calendar' }" />

      <!-- "Optimization Settings" header + search input — always visible so
           the user can type, refine, or clear from either view. -->
      <div class="bs-optimizations-head">
        <tidy-text [header]="'Optimization Settings'" class="bs-optimizations-head__title bold" />
        <div [formGroup]="searchForm" class="bs-optimizations-head__search">
          <tidy-search-input
            formControlName="search"
            [placeholder]="'Search settings'"
            [icon]="'assets/img/search-header.svg'"
            class="header-search"
            [showClearButton]="true"
            (clear)="clearSearch()" />
        </div>
      </div>

      <!-- Search results — only mounts while searching so the tidy-input
           components inside don't conflict with the always-mounted form
           controls in the tabbed cards below (7 short-term + 3 longer-term,
           only one tab's cards mounted at a time). Both branches use the same
           `sub` template via ngTemplateOutlet; if mounted simultaneously
           there would be duplicate FormControl bindings and the inputs
           would render empty. -->
      @if (isSearching) {
        <div class="bs-search-results">
          @for (row of matchedSubsectionRows; track row.id) {
            @if (row.groupLabel) {
              <div class="bs-group-head">
                <tidy-text [body]="row.groupLabel" class="bs-group-head__title bold" />
              </div>
            }
            <ng-container *ngTemplateOutlet="sub; context: { id: row.id }" />
          }
          @if (matchedSubsectionsExcludingCalendar.length === 0) {
            <tidy-alert class="black-background bs-search-empty">
              <tidy-text [helper]="'No results'" />
            </tidy-alert>
          }
        </div>
      }

      @if (!isSearching) {
      <!-- Tab bar — replaces the old always-visible group-label rows. Only the
           active tab's cards render below; the search view (above) ignores
           the tabs entirely and shows matches from both groups. Hidden entirely
           when long-term is off, since a single tab is just chrome. -->
      @if (settingsTabs.length > 1) {
        <div class="bs-tabs">
          <tidy-tabs
            [tabs]="settingsTabs"
            [activeTab]="settingsTab"
            (tabChange)="setSettingsTab($event)" />
        </div>
      }

      @if (settingsTab === 'short') {
        <!-- 1. Strategy optimization -->
        <optimization-section
          [num]="1"
          [icon]="'assets/svg/business-outline.svg'"
          [title]="'Strategy Optimization' | translate"
          [description]="'Set the min and max nights guests can book, with overrides for specific dates.'"
          [attr.data-section]="'strategy'"
          [(open)]="open.strategy">
          <ng-container *ngTemplateOutlet="sub; context: { id: 'strategy' }" />
        </optimization-section>

        <!-- 2. Price Optimization -->
        <optimization-section
          [num]="2"
          [icon]="'assets/svg/cash-outline.svg'"
          [title]="'Price Optimization' | translate"
          [description]="'See how current pricing stacks up and tap a day to inspect the layered breakdown.'"
          [attr.data-section]="'price'"
          [(open)]="open.price">
          <div class="bs-group">
            <ng-container *ngTemplateOutlet="sub; context: { id: 'price-range' }" />
          </div>
        </optimization-section>

        <!-- 3. Channel optimization -->
        <optimization-section
          [num]="3"
          [icon]="'assets/svg/globe-outline.svg'"
          [title]="'Channel Optimization' | translate"
          [description]="'Airbnb, VRBO, Booking.com, direct, and more — always in sync.'"
          [attr.data-section]="'channel'"
          [(open)]="open.channel">
          <div class="bs-group">
            <ng-container *ngTemplateOutlet="sub; context: { id: 'channel-instant-book' }" />
            <ng-container *ngTemplateOutlet="sub; context: { id: 'channel-non-refundable' }" />
            <ng-container *ngTemplateOutlet="sub; context: { id: 'channel-seasonal-cancellation' }" />
          </div>
        </optimization-section>

        <!-- 4. Rank-based optimization -->
        <optimization-section
          [num]="4"
          [icon]="'assets/svg/stats-chart-outline.svg'"
          [title]="'Rank-based Optimization' | translate"
          [description]="'Tracks your search rank for every open date.'"
          [attr.data-section]="'rank'"
          [(open)]="open.rank">
          <ng-container *ngTemplateOutlet="sub; context: { id: 'rank-tracker' }" />
        </optimization-section>

        <!-- 5. Discount optimization -->
        <optimization-section
          [num]="5"
          [icon]="'assets/svg/pricetag-outline.svg'"
          [title]="'Discount Optimization' | translate"
          [description]="'Turns on every platform discount — and still nets your target.'"
          [attr.data-section]="'discount'"
          [(open)]="open.discount">
          @if (discountSyncNote()) {
            <tidy-row>
              <tidy-text [helper]="discountSyncNote()" class="bs-sync-note" />
            </tidy-row>
          }
          <div class="bs-discount-grid">
            <ng-container *ngTemplateOutlet="sub; context: { id: 'discount-weekly' }" />
            <ng-container *ngTemplateOutlet="sub; context: { id: 'discount-monthly' }" />
            <ng-container *ngTemplateOutlet="sub; context: { id: 'discount-last-minute' }" />
            <ng-container *ngTemplateOutlet="sub; context: { id: 'discount-far-out' }" />
            <ng-container *ngTemplateOutlet="sub; context: { id: 'discount-member-only' }" />
            <ng-container *ngTemplateOutlet="sub; context: { id: 'discount-mobile' }" />
            <ng-container *ngTemplateOutlet="sub; context: { id: 'discount-flash' }" />
          </div>
        </optimization-section>

        <!-- 6. Misc optimization -->
        <optimization-section
          [num]="6"
          [icon]="'assets/svg/bed-outline.svg'"
          [title]="'Misc Optimization' | translate"
          [description]="'Check-in/out times, restricted days, and booking-rule lead times.'"
          [attr.data-section]="'minStay'"
          [(open)]="open.minStay">
          <div class="bs-group">
            <ng-container *ngTemplateOutlet="sub; context: { id: 'minstay-checkin-checkout' }" />
            <ng-container *ngTemplateOutlet="sub; context: { id: 'minstay-scheduling' }" />
          </div>
        </optimization-section>

        <!-- 7. Listing optimization -->
        <optimization-section
          [num]="7"
          [icon]="'assets/svg/home-outline.svg'"
          [title]="'Listing Optimization' | translate"
          [description]="'Title, description, amenities, photos and listing rules, refreshed to lift conversion and rank.'"
          [attr.data-section]="'listing'"
          [(open)]="open.listing"
          [connector]="false">
          <div class="bs-group">
            <ng-container *ngTemplateOutlet="sub; context: { id: 'listing-content' }" />
            <ng-container *ngTemplateOutlet="sub; context: { id: 'listing-pet-friendly' }" />
            <ng-container *ngTemplateOutlet="sub; context: { id: 'listing-cancellation-policy' }" />
          </div>
        </optimization-section>
      }

      @if (settingsTab === 'longer') {
        <!-- 1. Comps -->
        <optimization-section
          [num]="1"
          [icon]="'assets/svg/stats-chart-outline.svg'"
          [title]="'Comps' | translate"
          [description]="'Nearby mid-term and long-term comparable listings.'"
          [attr.data-section]="'mlComps'"
          [(open)]="open.mlComps">
          <ng-container *ngTemplateOutlet="sub; context: { id: 'ml-comps' }" />
        </optimization-section>

        <!-- 2. Target rent -->
        <optimization-section
          [num]="2"
          [icon]="'assets/svg/cash-outline.svg'"
          [title]="'Target Rent' | translate"
          [description]="'Target rent and floors for each term, with a comp-derived recommendation.'"
          [attr.data-section]="'mlTarget'"
          [(open)]="open.mlTarget">
          <ng-container *ngTemplateOutlet="sub; context: { id: 'ml-target-rent' }" />
        </optimization-section>

        <!-- 3. Listing optimization (mid/long channels) -->
        <optimization-section
          [num]="3"
          [icon]="'assets/svg/home-outline.svg'"
          [title]="'Listing Optimization' | translate"
          [description]="'Completeness and status across Zillow, Apartments.com, Zumper, and Furnished Finder.'"
          [attr.data-section]="'mlListing'"
          [(open)]="open.mlListing"
          [connector]="false">
          <ng-container *ngTemplateOutlet="sub; context: { id: 'ml-listing-optimization' }" />
        </optimization-section>
      }
      }
      }

    </form>
    } @else {
    <!-- First-load skeleton — mirrors the loaded layout exactly (same
         wrapper/section classes and dimensions) so nothing shifts when the
         real content swaps in: price toolbar + 5-week compact calendar,
         "Optimization Settings" head + search box, the tab bar, then the
         short-term tab's 7 collapsed numbered sections (the
         tab default), then the "Optimization Reports" head + empty-state
         alert. Placeholder widths vary per cell/section (data arrays +
         nth-child CSS) so the lines read like real dynamic text. -->
    <div class="bs-stack bs-skeleton" [class.bs-scroll]="windowService.isDesktopRes">
      <div class="bs-calendar-block sk-section">
        <div class="bs-price-toolbar">
          <ion-skeleton-text [animated]="true" class="sk-cal-arrow" />
          <ion-skeleton-text [animated]="true" class="sk-cal-range" />
          <ion-skeleton-text [animated]="true" class="sk-cal-arrow" />
        </div>
        <div class="sk-cal">
          <div class="sk-cal-weekdays">
            @for (w of skWeekdayWidths; track $index) {
              <div>
                <ion-skeleton-text [animated]="true" class="sk-cal-weekday" [style.width.px]="w" />
              </div>
            }
          </div>
          <div class="sk-cal-days">
            @for (cell of skCalendarCells; track $index) {
              <div class="sk-cal-day">
                <ion-skeleton-text [animated]="true" class="sk-cal-daynum" [style.width.px]="cell.day" />
                @if (cell.price) {
                  <ion-skeleton-text
                    [animated]="true"
                    class="sk-cal-price"
                    [class.sk-cal-price--dim]="cell.dim"
                    [style.width.px]="cell.price" />
                }
                @if (cell.hint) {
                  <ion-skeleton-text [animated]="true" class="sk-cal-hint" />
                }
              </div>
            }
          </div>
        </div>
      </div>

      <div class="bs-optimizations-head sk-section">
        <ion-skeleton-text [animated]="true" class="sk-head-title" />
        <ion-skeleton-text [animated]="true" class="sk-search" />
      </div>

      <div class="bs-tabs sk-section">
        <ion-skeleton-text [animated]="true" class="sk-tab" />
        <ion-skeleton-text [animated]="true" class="sk-tab" />
      </div>

      @for (i of skSections; track i) {
        <div class="opt-section sk-section" [class.opt-section--connect]="i !== 7">
          <div class="opt-section__head sk-section-head">
            <ion-skeleton-text [animated]="true" class="sk-badge" />
            <span class="opt-section__heading">
              <ion-skeleton-text [animated]="true" class="sk-section-title" />
              <ion-skeleton-text [animated]="true" class="sk-section-sub" />
            </span>
            <ion-skeleton-text [animated]="true" class="sk-chev" />
          </div>
        </div>
      }
    </div>
    }
  </tidy-wrapper>
</ng-template>

<!-- The listing builder's copy: the short-term fields as plain cards below its Photos card,
     saved by the builder's own Save button. -->
<ng-template #embeddedContent>
  @if (!isDemo) {
    <div [formGroup]="bookingForm" class="bs-embedded">
      <tidy-card [attr.data-section]="'strategy'">
        <tidy-row class="extra-bottom-padding">
          <tidy-text [header]="'Stay Length'" />
        </tidy-row>
        <ng-container *ngTemplateOutlet="sub; context: { id: 'strategy' }" />
      </tidy-card>

      <tidy-card [attr.data-section]="'price'">
        <tidy-row class="extra-bottom-padding">
          <tidy-text [header]="'Pricing'" />
        </tidy-row>
        <div class="bs-group">
          <ng-container *ngTemplateOutlet="sub; context: { id: 'price-range' }" />
          <ng-container *ngTemplateOutlet="sub; context: { id: 'listing-pet-fee' }" />
        </div>
      </tidy-card>

      <tidy-card [attr.data-section]="'discount'">
        <tidy-row class="extra-bottom-padding">
          <tidy-text [header]="'Discounts'" />
        </tidy-row>
        @if (discountSyncNote()) {
          <tidy-row>
            <tidy-text [helper]="discountSyncNote()" class="bs-sync-note" />
          </tidy-row>
        }
        <div class="bs-discount-grid">
          <ng-container *ngTemplateOutlet="sub; context: { id: 'discount-weekly' }" />
          <ng-container *ngTemplateOutlet="sub; context: { id: 'discount-monthly' }" />
          <ng-container *ngTemplateOutlet="sub; context: { id: 'discount-last-minute' }" />
          <ng-container *ngTemplateOutlet="sub; context: { id: 'discount-far-out' }" />
          <ng-container *ngTemplateOutlet="sub; context: { id: 'discount-member-only' }" />
          <ng-container *ngTemplateOutlet="sub; context: { id: 'discount-mobile' }" />
          <ng-container *ngTemplateOutlet="sub; context: { id: 'discount-flash' }" />
        </div>
      </tidy-card>

      <tidy-card [attr.data-section]="'channel'">
        <tidy-row class="extra-bottom-padding">
          <tidy-text [header]="'Booking Rules'" />
        </tidy-row>
        <div class="bs-group">
          <ng-container *ngTemplateOutlet="sub; context: { id: 'channel-instant-book' }" />
          <ng-container *ngTemplateOutlet="sub; context: { id: 'minstay-checkin-checkout' }" />
          <ng-container *ngTemplateOutlet="sub; context: { id: 'minstay-scheduling' }" />
          <ng-container *ngTemplateOutlet="sub; context: { id: 'channel-seasonal-cancellation' }" />
        </div>
      </tidy-card>
    </div>
  }
</ng-template>

@if (embedded()) {
  <ng-container *ngTemplateOutlet="embeddedContent" />
} @else {
  <app-scroll-wrapper [isfullWidthContent]="true" [templateContent]="content" />
}
